import apiClient from '@/lib/api/apiClient'
import type { EmailTemplateResponse } from '../types/api'
import { throwServiceError } from './serviceHelpers'

/** Send application email — POST /api/email_send/application/:id. */
export async function sendApplicationEmail(
  applicantId: string | number,
  emailType: string,
): Promise<void> {
  try {
    await apiClient.post(`/api/email_send/application/${applicantId}`, {
      application_status: emailType,
    })
  } catch (error) {
    throwServiceError(error, 'Failed to send email.')
  }
}

/** Resend denial email with denied-form fields (Angular sendDeniedEmail). */
export async function sendDeniedApplicationResendEmail(
  applicantId: string | number,
  payload: {
    application_status: 'DENIED_APPLICATION'
    send_cancelled_mail?: string
    reason_type: string
    notes: string
    checkbox_data: Record<string, unknown>
  },
): Promise<void> {
  try {
    await apiClient.post(`/api/email_send/application/${applicantId}`, {
      ...payload,
      send_cancelled_mail: payload.send_cancelled_mail ?? '',
    })
  } catch (error) {
    throwServiceError(error, 'Failed to send denial email.')
  }
}

/** Send error mail — POST /api/email_send/application/:id. */
export async function sendErrorMail(
  applicantId: string | number,
  errorType: string,
): Promise<void> {
  try {
    await apiClient.post(`/api/email_send/application/${applicantId}`, {
      application_status: errorType,
    })
  } catch (error) {
    throwServiceError(error, 'Failed to send error email.')
  }
}

/** Credit report error email — POST /api/email_credit_error (Angular EmailCreditErrorComponent). */
export async function sendCreditErrorEmail(payload: {
  applicant_id: string | number
  security_freeze: boolean
  credit_error: boolean
}): Promise<void> {
  try {
    await apiClient.post('/api/email_credit_error', payload)
  } catch (error) {
    throwServiceError(error, 'Failed to send credit-error email.')
  }
}

/** Broadcast email — POST /api/email_broadcast multipart (Angular EmailBroadcastComponent). */
export async function sendBroadcastEmail(formData: FormData): Promise<void> {
  try {
    await apiClient.post('/api/email_broadcast', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  } catch (error) {
    throwServiceError(error, 'Failed to send email.')
  }
}

export async function fetchEmailTemplate(
  templateKey: string,
): Promise<EmailTemplateResponse> {
  try {
    const response = await apiClient.get<EmailTemplateResponse>(
      `/api/get_email_templates/${templateKey}`,
    )
    return (response.data ?? {}) as EmailTemplateResponse
  } catch (error) {
    throwServiceError(error, 'Failed to load email template.')
  }
}

/** Missing info email — POST /api/email_missing_info (Angular EmailMissingInfoComponent). */
export async function sendMissingInfoEmail(formData: FormData): Promise<void> {
  try {
    await apiClient.post('/api/email_missing_info', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  } catch (error) {
    throwServiceError(error, 'Failed to send missing-info email.')
  }
}

/** Send application link via email or SMS. */
export async function sendApplicationLink(
  applicantId: string | number,
  type: 'email' | 'sms',
): Promise<void> {
  try {
    await apiClient.post(`/api/email_send/application/${applicantId}`, {
      application_status: 'INCOMPLETE_APPLICATION',
      flag: type,
    })
  } catch (error) {
    throwServiceError(error, 'Failed to send application link.')
  }
}

export async function getMailTrace(applicantId: string | number): Promise<unknown[]> {
  try {
    const response = await apiClient.get<unknown[]>(
      `/api/application/mail_trace/${applicantId}`,
    )
    return Array.isArray(response.data) ? response.data : []
  } catch (error) {
    throwServiceError(error, 'Failed to load mail trace.')
  }
}

export async function getSmsTrace(applicantId: string | number): Promise<unknown[]> {
  try {
    const response = await apiClient.get<unknown[]>(
      `/api/application/sms_trace/${applicantId}`,
    )
    return Array.isArray(response.data) ? response.data : []
  } catch (error) {
    throwServiceError(error, 'Failed to load SMS trace.')
  }
}
